import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Briefcase } from "@phosphor-icons/react";

export default function PostJob() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        title: "",
        description: "",
        requirements: "",
        location: "",
        job_type: "full-time",
        salary_min: "",
        salary_max: "",
        skills: "",
        experience_required: 0,
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                salary_min: form.salary_min ? Number(form.salary_min) : null,
                salary_max: form.salary_max ? Number(form.salary_max) : null,
                experience_required: Number(form.experience_required) || 0,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
            };
            await api.post("/jobs", payload);
            toast.success("Job posted.");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to post job");
        } finally {
            setLoading(false);
        }
    };

    const Field = ({ label, children }) => (
        <div>
            <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                {label}
            </label>
            {children}
        </div>
    );

    return (
        <div data-testid="post-job-page">
            <Navbar />
            <section className="max-w-[900px] mx-auto px-6 py-10">
                <div className="flex items-center gap-2 font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                    <Briefcase size={14} weight="bold" /> New role
                </div>
                <h1 className="font-display text-4xl">Post a job</h1>
                <p className="text-slate-600 mt-2">
                    The more specific your requirements, the sharper the match scores.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="post-job-form">
                    <Field label="Title">
                        <input
                            data-testid="post-job-title"
                            required value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                        />
                    </Field>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Location">
                            <input
                                data-testid="post-job-location"
                                required value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </Field>
                        <Field label="Job type">
                            <select
                                data-testid="post-job-type"
                                value={form.job_type}
                                onChange={(e) => setForm({ ...form, job_type: e.target.value })}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none bg-white"
                            >
                                <option value="full-time">Full-time</option>
                                <option value="part-time">Part-time</option>
                                <option value="contract">Contract</option>
                                <option value="internship">Internship</option>
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Salary min (₹ per year)">
                            <input
                                data-testid="post-job-salary-min"
                                type="number" value={form.salary_min}
                                onChange={(e) => setForm({ ...form, salary_min: e.target.value })}
                                placeholder="e.g. 800000"
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </Field>
                        <Field label="Salary max (₹ per year)">
                            <input
                                data-testid="post-job-salary-max"
                                type="number" value={form.salary_max}
                                onChange={(e) => setForm({ ...form, salary_max: e.target.value })}
                                placeholder="e.g. 1500000"
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </Field>
                        <Field label="Min experience (years)">
                            <input
                                data-testid="post-job-experience"
                                type="number" value={form.experience_required}
                                onChange={(e) => setForm({ ...form, experience_required: e.target.value })}
                                className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                            />
                        </Field>
                    </div>

                    <Field label="Required skills (comma-separated)">
                        <input
                            data-testid="post-job-skills"
                            value={form.skills}
                            onChange={(e) => setForm({ ...form, skills: e.target.value })}
                            placeholder="React, Node.js, PostgreSQL"
                            className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                        />
                    </Field>

                    <Field label="Description">
                        <textarea
                            data-testid="post-job-description"
                            required rows={6} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                        />
                    </Field>
                    <Field label="Requirements">
                        <textarea
                            data-testid="post-job-requirements"
                            required rows={5} value={form.requirements}
                            onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                            className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                        />
                    </Field>

                    <button
                        data-testid="post-job-submit"
                        disabled={loading}
                        className="bg-slate-950 text-white px-6 py-3 hover:bg-blue-600 disabled:opacity-60"
                    >
                        {loading ? "Posting…" : "Post job"}
                    </button>
                </form>
            </section>
            <Footer />
        </div>
    );
}
